import { z } from 'zod';

/**
 * Workflow de rendez-vous (phase 1).
 *
 * Le client demande un RDV → BCL propose 2 créneaux par email → le client en
 * choisit un via un lien public → BCL attribue le RDV à un technicien ou un
 * partenaire → le destinataire accepte / refuse (motif) puis réalise / non
 * effectué. Un refus renvoie le RDV à l'admin pour réattribution prioritaire.
 */

export const appointmentStatusSchema = z.enum([
  'demande',
  'creneaux_proposes',
  'confirme',
  'attribue',
  'accepte',
  'refuse',
  'realise',
  'non_effectue',
]);
export type AppointmentStatus = z.infer<typeof appointmentStatusSchema>;

// --- Client ---

export const requestAppointmentSchema = z.object({
  projectId: z.string().uuid(),
  message: z.string().trim().max(1000).optional(),
});
export type RequestAppointmentInput = z.infer<typeof requestAppointmentSchema>;

// --- Admin : proposer 2 créneaux ---

export const proposeSlotsSchema = z.object({
  slots: z
    .array(z.string().datetime())
    .min(1, 'Au moins un créneau')
    .max(2, 'Deux créneaux maximum'),
});
export type ProposeSlotsInput = z.infer<typeof proposeSlotsSchema>;

// --- Client : confirmer un créneau (lien public à token) ---

export const confirmSlotSchema = z.object({
  token: z.string().min(10),
  slotIndex: z.number().int().min(0).max(1),
});
export type ConfirmSlotInput = z.infer<typeof confirmSlotSchema>;

// Confirmation depuis l'espace client (authentifié) : pas de token, on
// identifie le rendez-vous par son id et on vérifie la propriété du dossier.
export const chooseSlotSchema = z.object({
  slotIndex: z.number().int().min(0).max(1),
});
export type ChooseSlotInput = z.infer<typeof chooseSlotSchema>;

// --- Admin : attribuer à un technicien ou partenaire ---

export const assignAppointmentSchema = z.object({
  assigneeId: z.string().uuid(),
});
export type AssignAppointmentInput = z.infer<typeof assignAppointmentSchema>;

// --- Destinataire : accepter / refuser ---

export const respondAppointmentSchema = z
  .object({
    decision: z.enum(['accepte', 'refuse']),
    reason: z.string().trim().max(500).optional(),
  })
  .refine((d) => d.decision === 'accepte' || !!d.reason, {
    message: 'Motif requis pour un refus',
    path: ['reason'],
  });
export type RespondAppointmentInput = z.infer<typeof respondAppointmentSchema>;

// --- Destinataire : clôturer ---

export const completeAppointmentSchema = z
  .object({
    decision: z.enum(['realise', 'non_effectue']),
    reason: z.string().trim().max(500).optional(),
  })
  .refine((d) => d.decision === 'realise' || !!d.reason, {
    message: 'Motif requis si non effectué',
    path: ['reason'],
  });
export type CompleteAppointmentInput = z.infer<typeof completeAppointmentSchema>;

export const appointmentRequestSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  status: appointmentStatusSchema,
  message: z.string().nullable(),
  proposedSlots: z.array(z.string()),
  confirmedSlot: z.string().nullable(),
  assigneeId: z.string().uuid().nullable(),
  refusalReason: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type AppointmentRequest = z.infer<typeof appointmentRequestSchema>;
